import { cn } from "@/lib/utils";

/**
 * Following — مساران في `following/page.tsx`:
 * 1) لا فيديوهات من المتابَعين: `max-w-lg` + عنوان/وصف + `SuggestedUsers` (عنوان قسم + بطاقات).
 * 2) يوجد فيديو: شبكة + `VideoFeed` + عمود فارغ 380px.
 *
 * الـ skeleton الافتراضي يطابق (1) لأنه المحتوى البديل عند عدم وجود ريلز.
 * مسار الفيديو: `FollowingVideoFeedSkeleton`.
 */

const SUGGESTED_ROW_COUNT = 6;

function FollowingEmptyStateSkeleton() {
  return (
    <div
      className="skeleton-stage max-w-lg w-full mx-auto px-4 mt-8 max-h-[calc(100vh-65px)] lg:max-h-[calc(100vh-104px)]"
      aria-busy
      aria-label="Loading"
    >
      {/* يطابق h2 + p في حالة following-no-content */}
      <div className="flex flex-col items-center text-center mb-8">
        <div className="h-8 w-56 max-w-[90%] rounded-lg skeleton-shimmer mb-3" />
        <div className="h-4 w-full max-w-sm rounded-md skeleton-shimmer mb-2" />
        <div className="h-4 w-full max-w-[240px] mx-auto rounded-md skeleton-shimmer" />
      </div>

      <div className="h-full w-full relative">
        {/* يطابق h3 "suggested-to-follow" */}
        <div className="h-7 w-48 rounded-md skeleton-shimmer mb-4" />
        <div className="flex flex-col gap-4">
          {Array.from({ length: SUGGESTED_ROW_COUNT }, (_, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="h-12 w-12 rounded-full skeleton-shimmer shrink-0" />
                <div className="flex flex-col gap-2 min-w-0">
                  <div className="h-4 w-36 max-w-full rounded-md skeleton-shimmer" />
                  <div className="h-3 w-28 max-w-full rounded-md skeleton-shimmer opacity-90" />
                </div>
              </div>
              <div className="h-9 w-[5.5rem] rounded-full skeleton-shimmer shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * شبكة الفيديو + عمود جانبي فارغ — يطابق `FollowingPage` عند وجود ريل (بدون تبويبات، بدون مقترحين).
 * العمود الأيمن: مساحة محجوزة ~380px كما في الصفحة الفعلية.
 */
export function FollowingVideoFeedSkeleton() {
  return (
    <div
      className={cn(
        "skeleton-stage mx-auto grid min-h-0 w-full max-w-screen grid-cols-1 grid-rows-1 place-items-center gap-6 px-4",
        "max-lg:flex max-lg:min-h-0 max-lg:flex-1 max-lg:max-w-none max-lg:gap-0 max-lg:px-0",
        "max-lg:h-[calc(100svh-65px-env(safe-area-inset-bottom,0px))]",
        "max-lg:max-h-[calc(100svh-65px-env(safe-area-inset-bottom,0px))]",
        "lg:grid lg:grid-cols-2 lg:h-[calc(100vh-104px)] lg:max-h-[calc(100vh-104px)] lg:w-auto lg:max-w-7xl",
      )}
      aria-busy
      aria-label="Loading"
    >
      <div className="relative col-span-2 h-full min-h-0 w-full max-lg:flex-1 lg:col-span-1 lg:flex lg:justify-end">
        <div
          className={cn(
            "relative h-full min-h-0 w-full max-lg:max-h-full",
            "lg:ms-auto lg:aspect-[9/16] lg:h-[min(100%,calc(100vh-104px))] lg:max-h-[calc(100vh-104px)] lg:max-w-lg lg:min-h-[320px] lg:w-auto lg:shrink-0",
          )}
        >
          <div className="h-full max-lg:pt-0 pt-4">
            <div className="h-full max-lg:rounded-none overflow-hidden rounded-t-2xl skeleton-shimmer opacity-95" />
          </div>
          <div className="absolute bottom-28 end-2 z-30 flex w-10 flex-col gap-2 items-center lg:bottom-0 lg:end-0 lg:ms-4 lg:start-full">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-9 w-9 shrink-0 rounded-full skeleton-shimmer sm:h-10 sm:w-10"
              />
            ))}
          </div>
        </div>
      </div>

      <div
        className="hidden lg:flex py-4 h-full w-[380px] min-h-0 shrink-0 rounded-2xl border border-dashed border-border/45 bg-muted/15"
        aria-hidden
      />
    </div>
  );
}

/** @deprecated استخدم `FollowingVideoFeedSkeleton` — الافتراضي يطابق مسار الفيديو وليس الحالة الفارغة */
export default function FollowingFeedPageSkeleton() {
  return <FollowingVideoFeedSkeleton />;
}
